import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatUsPhoneInput } from '../utils/phone';
import { Loader2, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { getAuthToken, storeAuthToken } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealerRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // If ?plan=trial|monthly|yearly|lifetime is present, skip the package picker
  // (step 2) and route the dealer straight to PayPal for that plan after
  // their account is created in step 1.
  const presetPlan = searchParams.get('plan') || null;
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    business_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipcode: ''
  });
  const [registrationResult, setRegistrationResult] = useState(null);

  const handleChange = (e) => {
    if (e.target.name === 'phone') {
      setFormData({ ...formData, phone: formatUsPhoneInput(e.target.value) });
      return;
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const digitsOnlyPhone = formData.phone ? formData.phone.replace(/\D/g, '').slice(-10) : '';

      const response = await axios.post(`${API}/dealer/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        business_name: formData.business_name,
        phone: digitsOnlyPhone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipcode: formData.zipcode
      });

      storeAuthToken(response.data.token, true); // Persistent login for new registrations
      setRegistrationResult(response.data);
      setStep(2);
      toast.success('Account created successfully!');

      // If the dealer landed here from a "Get Started" button on /dealers
      // (?plan=trial|monthly|yearly|lifetime), skip the package picker entirely
      // and send them straight to PayPal — we already have their info.
      if (presetPlan) {
        // Wait one tick so the toast renders before the redirect
        setTimeout(() => handleSubscribe(presetPlan, response.data.token), 200);
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setIsLoading(false);
    }
  };

  const plans = [
    { id: 'trial', name: '7-Day Trial', price: '$7', description: 'Try it for a week, auto-converts to monthly', highlight: true, badge: 'START HERE' },
    { id: 'monthly', name: 'Monthly', price: '$49.99/mo', description: 'Perfect for getting started' },
    { id: 'yearly', name: 'Yearly', price: '$499.99/yr', description: 'Save $100 annually', popular: true },
    { id: 'lifetime', name: 'Lifetime', price: '$999.99', description: 'One-time payment, forever access' }
  ];

  const handleSubscribe = async (plan, tokenOverride = null) => {
    setIsLoading(true);
    try {
      const token = tokenOverride || getAuthToken();
      const response = await axios.post(
        `${API}/subscription/create?plan=${plan}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to PayPal
      window.location.href = response.data.approval_url;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="dealer-register-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 
            className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-[#004E86] mb-4"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Become a Dealer
          </h1>
          <p className="text-lg text-gray-700">
            Get your own personalized RV application link and start capturing leads
          </p>
          {presetPlan && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F2A900] text-black font-semibold text-sm" data-testid="preset-plan-banner">
              <Check size={16} />
              {(() => {
                const map = { trial: 'Selected: 7-Day Trial — $7', monthly: 'Selected: Monthly — $49.99/mo', yearly: 'Selected: Yearly — $499.99/yr', lifetime: 'Selected: Lifetime — $999.99' };
                return map[presetPlan] || `Selected plan: ${presetPlan}`;
              })()} — straight to PayPal after sign-up
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-[#004E86] text-white' : 'bg-gray-300 text-gray-600'}`}>
              {step > 1 ? <Check size={20} /> : '1'}
            </div>
            <div className={`w-24 h-1 ${step > 1 ? 'bg-[#004E86]' : 'bg-gray-300'}`} />
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-[#004E86] text-white' : 'bg-gray-300 text-gray-600'}`}>
              2
            </div>
          </div>
        </div>

        {step === 1 && (
          <div className="bg-white shadow-xl rounded-2xl border-t-4 border-[#F2A900] p-8">
            <h2 className="text-2xl font-bold text-[#004E86] mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              Create Your Account
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    required
                    data-testid="business-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    data-testid="password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="phone-input"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    maxLength={2}
                    data-testid="state-input"
                  />
                </div>
                <div>
                  <Label htmlFor="zipcode">Zipcode</Label>
                  <Input
                    id="zipcode"
                    name="zipcode"
                    value={formData.zipcode}
                    onChange={handleChange}
                    maxLength={5}
                    data-testid="zipcode-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#F2A900] text-black hover:bg-[#D99700] font-bold uppercase tracking-wide py-3"
                data-testid="register-button"
              >
                {isLoading ? <Loader2 className="animate-spin mx-auto" /> : 'Create Account'}
              </Button>
            </form>
          </div>
        )}

        {step === 2 && registrationResult && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-green-800 mb-2">Account Created!</h3>
              <p className="text-green-700">Your unique quiz link:</p>
              <code className="block mt-2 p-3 bg-white rounded border text-[#004E86] font-mono">
                {window.location.origin}{registrationResult.quiz_link}
              </code>
            </div>

            <div className="bg-white shadow-xl rounded-2xl border-t-4 border-[#F2A900] p-8">
              <h2 className="text-2xl font-bold text-[#004E86] mb-6 text-center" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                Choose Your Plan
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {plans.map((plan) => (
                  <div 
                    key={plan.id}
                    className={`relative border-2 rounded-lg p-6 text-center transition-all hover:shadow-lg ${
                      plan.highlight 
                        ? 'border-[#004E86] bg-gradient-to-br from-[#004E86] to-[#00619D] text-white scale-105 shadow-xl' 
                        : plan.popular 
                          ? 'border-[#F2A900] bg-yellow-50' 
                          : 'border-gray-200'
                    }`}
                  >
                    {(plan.popular || plan.badge) && (
                      <span className={`absolute -top-3 left-1/2 transform -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full ${
                        plan.highlight ? 'bg-[#F2A900] text-black' : 'bg-[#F2A900] text-black'
                      }`}>
                        {plan.badge || 'POPULAR'}
                      </span>
                    )}
                    <h3 className={`text-xl font-bold ${plan.highlight ? 'text-white' : 'text-[#004E86]'}`}>{plan.name}</h3>
                    <p className={`text-3xl font-bold my-4 ${plan.highlight ? 'text-white' : ''}`}>{plan.price}</p>
                    <p className={`text-sm mb-4 ${plan.highlight ? 'text-white/80' : 'text-gray-600'}`}>{plan.description}</p>
                    <Button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={isLoading}
                      className={`w-full ${
                        plan.highlight
                          ? 'bg-[#F2A900] text-black hover:bg-[#D99700]'
                          : plan.popular 
                            ? 'bg-[#F2A900] text-black hover:bg-[#D99700]' 
                            : 'bg-[#004E86] text-white hover:bg-[#003d6b]'
                      } font-bold`}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {isLoading ? <Loader2 className="animate-spin" size={20} /> : 'Subscribe'}
                    </Button>
                  </div>
                ))}
              </div>

              <p className="text-center text-gray-500 text-sm mt-6">
                Secure payment powered by PayPal
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealerRegister;
